import { useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { serversApi } from '../../api'
import { getErrorMessage } from '../../api/client'
import Toast from '../../components/Toast'
import type { EdgeConfigResult, EdgeLine, EdgeMachine } from '../../types'
import LiveView from './LiveView'
import MachineSettings from './MachineSettings'
import { LineDialog, MachineDialog } from './MasterGroupDialogs'
import QueryView from './QueryView'
import SpecMasterImportDialog from './SpecMasterImportDialog'
import { useEdgeDevices, useEdgeMachines, useLiveTagValues } from './shared'
import { specTagLookup, useServerSpec } from './specLookup'
import { SegmentButton } from './ui'

type SubView = 'settings' | 'live' | 'query'

type DialogState =
  | { kind: 'line'; line?: EdgeLine }
  | { kind: 'machine'; line: EdgeLine; machine?: EdgeMachine }
  | { kind: 'spec' }
  | null

type ToastState = { message: string; type: 'success' | 'error' } | null

// 마스터데이터 탭: 왼쪽 라인 › 설비 트리, 오른쪽 선택한 설비의 [설정]·[실시간]·[조회].
// [설정](태그 연결)과 라인·설비 추가/수정/삭제는 Edge팀만 — API 도 Edge팀이 아니면 설비 이름만 준다.
// 수정은 모니터링이 멈춰 있을 때만 (lockReason 이 있으면 수정 버튼을 막는다 — 엣지도 실행 중이면 거부한다).
// Edge팀은 설비를 고르면 [실시간] 대신 [설정]을 본다 — 설정에 태그 현재 값이 실시간으로 나온다.
// 설비 목록을 못 받아도(엣지 오프라인 등) "전체 설비"의 실시간·조회는 쓸 수 있다 (EM 에 저장된 로그라서).
export default function MasterDataTab({ serverId, canViewSettings, lockReason }: {
  serverId: string
  canViewSettings: boolean
  lockReason: string | null
}) {
  const qc = useQueryClient()
  const [selectedCode, setSelectedCode] = useState<string | null>(null)
  const [sub, setSub] = useState<SubView>('live')
  const [dialog, setDialog] = useState<DialogState>(null)
  const [toast, setToast] = useState<ToastState>(null)

  const machinesQuery = useEdgeMachines(serverId)
  const lines = machinesQuery.data ?? []
  const machine = lines.flatMap(l => l.machines).find(m => m.code === selectedCode) ?? null
  const lineOf = (m: EdgeMachine) => lines.find(l => l.machines.some(x => x.code === m.code))

  const settingsAvailable = canViewSettings && machine != null
  const views: SubView[] = settingsAvailable ? ['settings', 'query'] : ['live', 'query']
  const view: SubView = views.includes(sub) ? sub : views[0]
  const showSettings = view === 'settings' && machine != null

  // 설정을 볼 때만 디바이스(태그 설명·마지막 값)를 받고 태그 값 스트림을 켠다
  const devicesQuery = useEdgeDevices(serverId, canViewSettings)
  const live = useLiveTagValues(showSettings ? serverId : undefined)
  // 이 서버에서 고른 명세서 — 항목 코드 기본값 {설비코드}_{Alias} 에서 Edge 태그 Alias 가 비었을 때 대신 쓴다 (디바이스 코드 + 주소로 짝짓기)
  const { specId, setSpecId, spec } = useServerSpec(serverId, canViewSettings)
  const devices = devicesQuery.isError ? undefined : devicesQuery.data
  const specTagOf = useMemo(() => specTagLookup(devices, spec), [devices, spec])

  // 엣지에 저장한 뒤 — 설비 목록을 다시 받는다 (EM 은 마스터데이터를 저장하지 않음).
  // success 가 false 면 일부만 저장된 경우 (목록 옮기기에서 원래 목록 빼기 실패)
  const onConfigSaved = (result: EdgeConfigResult) => {
    qc.invalidateQueries({ queryKey: ['servers', serverId, 'machines'] })
    setToast({ message: result.message, type: result.success ? 'success' : 'error' })
  }

  const runDelete = async (call: () => Promise<{ data: EdgeConfigResult }>, after?: () => void) => {
    try {
      const { data } = await call()
      after?.()
      onConfigSaved(data)
    } catch (err) {
      setToast({ message: getErrorMessage(err, '삭제하지 못했습니다.'), type: 'error' })
    }
  }

  const deleteLine = (line: EdgeLine) => {
    const extra = line.machines.length > 0 ? `\n안의 설비 ${line.machines.length}개와 그 신호 설정도 함께 삭제됩니다.` : ''
    if (!confirm(`'${line.name || line.code}' 라인을 삭제할까요?${extra}`)) return
    runDelete(() => serversApi.deleteLine(serverId, line.id), () => {
      if (line.machines.some(m => m.code === selectedCode)) setSelectedCode(null)
    })
  }

  const deleteMachine = (m: EdgeMachine) => {
    if (!confirm(`'${m.name}'(${m.code}) 설비를 삭제할까요?\n설비의 신호 설정도 함께 삭제됩니다. 이미 쌓인 설비 로그는 남습니다.`)) return
    runDelete(() => serversApi.deleteMachine(serverId, m.code), () => {
      if (m.code === selectedCode) setSelectedCode(null)
    })
  }

  return (
    <div className="lg:h-full lg:flex lg:gap-4">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <MachineTree
        lines={lines}
        selectedCode={selectedCode}
        onSelect={setSelectedCode}
        isFetching={machinesQuery.isFetching}
        error={machinesQuery.isError ? getErrorMessage(machinesQuery.error, '설비 목록을 불러오지 못했습니다.') : null}
        onRefresh={() => {
          machinesQuery.refetch()
          if (canViewSettings) devicesQuery.refetch()
        }}
        lockReason={lockReason}
        edit={canViewSettings && !machinesQuery.isError ? {
          onAddLine: () => setDialog({ kind: 'line' }),
          onEditLine: line => setDialog({ kind: 'line', line }),
          onDeleteLine: deleteLine,
          onAddMachine: line => setDialog({ kind: 'machine', line }),
          // 명세서 불러오기 — 명세서는 대화상자에서 고른다. Edge 태그를 받았을 때만
          onImportSpec: () => setDialog({ kind: 'spec' }),
          importSpecDisabled: !devices ? '디바이스 목록을 받지 못했습니다' : null,
        } : undefined}
      />

      <div className="flex-1 min-w-0 flex flex-col lg:h-full mt-4 lg:mt-0">
        <div className="flex flex-wrap items-center gap-3 mb-4 shrink-0">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-gray-800 truncate">{machine ? machine.name : '전체 설비'}</h3>
            {machine && <div className="text-xs text-gray-400 font-mono">{machine.code}</div>}
          </div>
          <div className="ml-auto flex gap-1 bg-gray-100 rounded-lg p-1">
            {settingsAvailable && <SegmentButton active={view === 'settings'} onClick={() => setSub('settings')}>설정</SegmentButton>}
            {!settingsAvailable && <SegmentButton active={view === 'live'} onClick={() => setSub('live')}>실시간</SegmentButton>}
            <SegmentButton active={view === 'query'} onClick={() => setSub('query')}>조회</SegmentButton>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto pb-1 -mx-1 px-1">
          {showSettings && (
            <>
              {devicesQuery.isError && (
                <div className="text-xs text-amber-600 mb-3">
                  {getErrorMessage(devicesQuery.error, '디바이스 목록을 불러오지 못했습니다.')} — 태그 설명·현재 값 없이 표시합니다.
                </div>
              )}
              <MachineSettings
                key={machine.code}
                serverId={serverId}
                machine={machine}
                devices={devices}
                specTagOf={specTagOf}
                live={live}
                onConfigSaved={onConfigSaved}
                lockReason={lockReason}
                onEditMachine={() => {
                  const line = lineOf(machine)
                  if (line) setDialog({ kind: 'machine', line, machine })
                }}
                onDeleteMachine={() => deleteMachine(machine)}
              />
            </>
          )}
          {/* 실시간은 설비를 바꿔도 다시 마운트하지 않는다 — 모아 둔 최신값을 설비 필터만 바꿔 보여준다 */}
          {view === 'live' && <LiveView serverId={serverId} machineCode={machine?.code} />}
          {view === 'query' && <QueryView key={machine?.code ?? '*'} serverId={serverId} machineCode={machine?.code} />}
        </div>
      </div>

      {dialog?.kind === 'line' && (
        <LineDialog serverId={serverId} line={dialog.line} onClose={() => setDialog(null)}
          onDone={result => { setDialog(null); onConfigSaved(result) }} />
      )}
      {dialog?.kind === 'machine' && (
        <MachineDialog serverId={serverId} line={dialog.line} machine={dialog.machine} onClose={() => setDialog(null)}
          onDone={result => {
            setDialog(null)
            onConfigSaved(result)
          }} />
      )}
      {dialog?.kind === 'spec' && devices && (
        <SpecMasterImportDialog serverId={serverId} initialSpecId={specId} onSpecChange={setSpecId} lines={lines} devices={devices}
          onClose={() => setDialog(null)}
          onDone={(result, machineCode) => {
            setDialog(null)
            onConfigSaved(result)
            setSelectedCode(machineCode)
            setSub('settings')
          }} />
      )}
    </div>
  )
}

interface TreeEdit {
  onAddLine: () => void
  onEditLine: (line: EdgeLine) => void
  onDeleteLine: (line: EdgeLine) => void
  onAddMachine: (line: EdgeLine) => void
  onImportSpec: () => void
  importSpecDisabled: string | null // 있으면 명세 불러오기를 막고 이유를 툴팁으로
}

function MachineTree({ lines, selectedCode, onSelect, isFetching, error, onRefresh, edit, lockReason }: {
  lines: EdgeLine[]
  selectedCode: string | null
  onSelect: (code: string | null) => void
  isFetching: boolean
  error: string | null
  onRefresh: () => void
  edit?: TreeEdit // Edge팀만 — 라인 추가/수정/삭제, 라인 안 설비 추가
  lockReason: string | null // 있으면 수정 버튼을 비활성하고 이유를 툴팁으로
}) {
  const locked = lockReason != null
  const count = lines.reduce((n, l) => n + l.machines.length, 0)

  return (
    <aside className="lg:w-64 shrink-0 bg-surface rounded-xl border border-gray-200 overflow-hidden flex flex-col lg:h-full max-h-80 lg:max-h-none">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 shrink-0">
        <span className="text-sm font-semibold text-gray-800">설비</span>
        {count > 0 && <span className="text-xs text-gray-400">{count}</span>}
        <div className="ml-auto flex items-center gap-3">
          <button onClick={onRefresh} disabled={isFetching}
            className="text-xs text-gray-500 hover:text-gray-700 disabled:opacity-50">
            {isFetching ? '불러오는 중…' : '새로고침'}
          </button>
          {edit && (
            <button onClick={edit.onImportSpec} disabled={locked || !!edit.importSpecDisabled}
              title={lockReason ?? edit.importSpecDisabled ?? '명세서를 골라 [마스터데이터]의 설비 항목을 불러옵니다'}
              className="text-xs text-blue-600 hover:text-blue-700 font-medium disabled:text-gray-300 disabled:cursor-not-allowed">명세</button>
          )}
          {edit && (
            <button onClick={edit.onAddLine} disabled={locked} title={lockReason ?? undefined}
              className="text-xs text-blue-600 hover:text-blue-700 font-medium disabled:text-gray-300 disabled:cursor-not-allowed">+ 라인</button>
          )}
        </div>
      </div>
      <div className="min-h-0 overflow-y-auto py-2">
        <TreeItem active={selectedCode === null} onClick={() => onSelect(null)}>
          <span className="font-medium">전체 설비</span>
        </TreeItem>

        {edit && lockReason && (
          <div className="mx-3 mb-1 mt-2 rounded-md bg-gray-50 px-2.5 py-1.5 text-[11px] text-gray-500">{lockReason}</div>
        )}

        {error ? (
          <div className="px-4 py-3 text-xs text-red-500">{error}</div>
        ) : lines.length === 0 ? (
          <div className="px-4 py-3 text-xs text-gray-400">
            {isFetching ? 'Edge에서 설비 목록을 받는 중…' : edit ? '라인이 없습니다. "+ 라인"으로 추가하세요.' : '등록된 설비가 없습니다.'}
          </div>
        ) : (
          lines.map(line => (
            <div key={line.id} className="mt-3">
              <div className="group flex items-center gap-1 px-4 pb-1">
                <span className="text-xs font-medium text-gray-500 truncate" title={line.description ?? undefined}>
                  {line.name || line.code || '(이름 없음)'}
                </span>
                {edit && (
                  // 라인 관리 — 마우스를 올렸을 때만 (터치 화면에서는 항상)
                  <span className="ml-auto flex items-center gap-0.5 shrink-0 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition">
                    <IconButton title="설비 추가" disabledReason={lockReason} onClick={() => edit.onAddMachine(line)}>
                      <path strokeLinecap="round" d="M12 5v14M5 12h14" />
                    </IconButton>
                    <IconButton title="라인 수정" disabledReason={lockReason} onClick={() => edit.onEditLine(line)}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 20h4L18.5 9.5a2.1 2.1 0 0 0-3-3L5 17v3z" />
                    </IconButton>
                    <IconButton title="라인 삭제" danger disabledReason={lockReason} onClick={() => edit.onDeleteLine(line)}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 7h12M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2m2 0-.8 12a2 2 0 0 1-2 1.9H8.8a2 2 0 0 1-2-1.9L6 7" />
                    </IconButton>
                  </span>
                )}
              </div>
              {line.machines.length === 0 ? (
                edit ? (
                  <button onClick={() => edit.onAddMachine(line)} disabled={locked} title={lockReason ?? undefined}
                    className="w-full text-left px-4 py-1 text-xs text-blue-600 hover:text-blue-700 disabled:text-gray-300 disabled:cursor-not-allowed">
                    + 설비 추가
                  </button>
                ) : (
                  <div className="px-4 py-1 text-xs text-gray-300">설비 없음</div>
                )
              ) : (
                line.machines.map(m => (
                  <TreeItem key={m.code} active={m.code === selectedCode} onClick={() => onSelect(m.code)}>
                    <span className="block truncate">{m.name}</span>
                    <span className="block text-[11px] text-gray-400 font-mono truncate">{m.code}</span>
                  </TreeItem>
                ))
              )}
            </div>
          ))
        )}
      </div>
    </aside>
  )
}

function IconButton({ title, danger, disabledReason, onClick, children }: {
  title: string
  danger?: boolean
  disabledReason?: string | null
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button onClick={onClick} disabled={!!disabledReason} title={disabledReason ?? title} aria-label={title}
      className={`p-1 rounded text-gray-400 hover:bg-gray-100 transition disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent ${danger ? 'hover:text-red-600' : 'hover:text-gray-700'}`}>
      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>{children}</svg>
    </button>
  )
}

function TreeItem({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick}
      className={`w-full text-left px-4 py-1.5 text-sm transition border-l-2 ${
        active ? 'border-blue-600 bg-blue-50 text-blue-700' : 'border-transparent text-gray-700 hover:bg-gray-50'
      }`}>
      {children}
    </button>
  )
}
